import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, requestOtp, verifyOtp, resetPassword } from '../services/api';

const Login = ({ setToken }) => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('login'); // login | requestOtp | verifyOtp | resetPassword
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login(formData);
      if (response.token) {
        setToken(response.token);
        const role = response.user?.role;
        if (role === 'professor') {
          navigate('/professor-dashboard');
        } else if (role === 'admin') {
          navigate('/admin-dashboard');
        } else {
          navigate('/leaves');
        }
      } else {
        setError(response.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during login.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await requestOtp({ email });
      setSuccess(response.message || 'OTP sent successfully.');
      setStep('verifyOtp');
    } catch (err) {
      setError(err.message || 'Failed to request OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await verifyOtp({ email, otp });
      setSuccess(response.message || 'OTP verified successfully.');
      setStep('resetPassword');
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await resetPassword({ email, otp, new_password: newPassword });
      setSuccess(response.message || 'Password reset successfully.');
      setStep('login');
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    switch (step) {
      case 'login':
        return (
          <form onSubmit={handleLogin} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>
            <button type="submit" style={styles.button} disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
            <p style={styles.footerText}>
              Forgot password?{' '}
              <a href="#" onClick={() => setStep('requestOtp')} style={styles.link}>
                Request OTP
              </a>
            </p>
          </form>
        );
      case 'requestOtp':
        return (
          <form onSubmit={handleRequestOtp} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
              />
            </div>
            <button type="submit" style={styles.button} disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Request OTP'}
            </button>
            <p style={styles.footerText}>
              <a href="#" onClick={() => setStep('login')} style={styles.link}>
                Back to Login
              </a>
            </p>
          </form>
        );
      case 'verifyOtp':
        return (
          <form onSubmit={handleVerifyOtp} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                style={styles.input}
              />
            </div>
            <button type="submit" style={styles.button} disabled={isLoading}>
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <p style={styles.footerText}>
              <a href="#" onClick={() => setStep('requestOtp')} style={styles.link}>
                Resend OTP
              </a>
            </p>
          </form>
        );
      case 'resetPassword':
        return (
          <form onSubmit={handleResetPassword} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={styles.input}
              />
            </div>
            <button type="submit" style={styles.button} disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Thogoto T.T.C Login</h2>
        {error && <p style={styles.errorText}>{error}</p>}
        {success && <p style={styles.successText}>{success}</p>}
        {renderForm()}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '80vh',
    backgroundColor: '#f8f8f8',
    fontFamily: "'Georgia', serif",
  },
  card: {
    backgroundColor: '#fff',
    padding: '2.5rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '450px',
    borderTop: '4px solid #A51C30',
  },
  title: {
    color: '#A51C30',
    textAlign: 'center',
    marginBottom: '1.5rem',
    fontSize: '1.8rem',
  },
  errorText: {
    color: '#c62828',
    textAlign: 'center',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  successText: {
    color: '#4caf50',
    textAlign: 'center',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#333',
    fontSize: '1rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    transition: 'border 0.3s ease',
  },
  button: {
    backgroundColor: '#A51C30',
    color: '#fff',
    padding: '0.75rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    marginTop: '1rem',
  },
  footerText: {
    textAlign: 'center',
    marginTop: '1.5rem',
    color: '#666',
  },
  link: {
    color: '#A51C30',
    textDecoration: 'none',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default Login;
